import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './Auth';
import { setAutoQualityCap, autoQualityCap, setPreferredAudio, preferredAudio } from './playback';

/**
 * The signed-in account's saved list and ratings — everything that isn't watch
 * history.
 *
 * The server keys this on the session, so each account has its own. It lives in
 * a single preferences document that the API writes whole, so the provider
 * keeps the untouched keys around and merges its own back in on every save.
 */
const LibraryContext = createContext({
  items: [],
  ratings: {},
  has: () => false,
  toggle: () => {},
  ratingFor: () => null,
  rate: () => {},
  maxAutoResolution: autoQualityCap(),
  setMaxAutoResolution: () => {},
  audioLanguage: preferredAudio(),
  setAudioLanguage: () => {}
});

const keyOf = (type, id) => `${type}:${id}`;

export const LibraryProvider = ({ children }) => {
  const [prefs, setPrefs] = useState({});
  const [items, setItems] = useState([]);
  const [ratings, setRatings] = useState({});
  const { user } = useAuth();

  // Read once there is a session to read it with, and again if that session
  // changes — signing in as someone else must not leave the previous list on
  // screen.
  useEffect(() => {
    if (!user) {
      setPrefs({});
      setItems([]);
      setRatings({});
      setPreferredAudio('original');
      return undefined;
    }

    let cancelled = false;
    axios
      .get('/api/library/preferences')
      .then(({ data }) => {
        if (cancelled) return;
        setPrefs(data.data || {});
        setItems(data.data?.myList || []);
        setRatings(data.data?.ratings || {});
        // Hand the quality cap to the playback lib, which has no context of
        // its own — an unset preference leaves the default in place.
        setAutoQualityCap(data.data?.maxAutoResolution);
        // Same for the default audio language: per profile, so switching
        // profile must re-apply it, and an unset one means original.
        setPreferredAudio(data.data?.audioLanguage);
      })
      .catch(() => {
        // A failed read shouldn't take the shell down; the next write re-syncs.
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Optimistic: these back single-click controls, and a failed write is
  // recoverable on the next one.
  const save = useCallback((patch) => {
    setPrefs((previous) => {
      const next = { ...previous, ...patch };
      axios.put('/api/library/preferences', next).catch(() => {});
      return next;
    });
  }, []);

  const toggle = useCallback(
    (title) => {
      const key = keyOf(title.type, title.id);
      const next = items.some((item) => keyOf(item.type, item.id) === key)
        ? items.filter((item) => keyOf(item.type, item.id) !== key)
        : [
            {
              type: title.type,
              id: title.id,
              name: title.name,
              year: title.year,
              rating: title.rating,
              poster: title.poster
            },
            ...items
          ];

      setItems(next);
      save({ myList: next });
    },
    [items, save]
  );

  const rate = useCallback(
    (title, score) => {
      const key = keyOf(title.type, title.id);
      const next = { ...ratings };
      // Clicking the star you already gave clears the rating
      if (next[key] === score) delete next[key];
      else next[key] = score;

      setRatings(next);
      save({ ratings: next });
    },
    [ratings, save]
  );

  // The playback lib is the authority on the applied value; saving is ours.
  const setMaxAutoResolution = useCallback(
    (choice) => {
      setAutoQualityCap(choice);
      save({ maxAutoResolution: autoQualityCap() });
    },
    [save]
  );

  const setAudioLanguage = useCallback(
    (choice) => {
      setPreferredAudio(choice);
      save({ audioLanguage: preferredAudio() });
    },
    [save]
  );

  const value = useMemo(
    () => ({
      items,
      ratings,
      has: (type, id) => items.some((item) => keyOf(item.type, item.id) === keyOf(type, id)),
      toggle,
      ratingFor: (type, id) => ratings[keyOf(type, id)] || null,
      rate,
      maxAutoResolution: prefs.maxAutoResolution || autoQualityCap(),
      setMaxAutoResolution,
      audioLanguage: prefs.audioLanguage || 'original',
      setAudioLanguage
    }),
    [items, ratings, toggle, rate, prefs.maxAutoResolution, setMaxAutoResolution, prefs.audioLanguage, setAudioLanguage]
  );

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
};

export const useLibrary = () => useContext(LibraryContext);
